import { Button, Grid, Typography } from "@material-ui/core";
import {
	ColDef,
	GetContextMenuItemsParams,
	GridApi,
	GridReadyEvent,
	MenuItemDef,
	RowDoubleClickedEvent,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import * as React from "react";
import { useEffect, useMemo, useState } from "react";
import { ErrorModal } from "../../../../../components/modals/error-modal";
import { LazyLoading } from "../../../../../controls/LazyLoading/LazyLoading";
import { useLogsContext } from "../../context/LogsContext";
import { Log } from "../../models/Log";
import { CodeFilter } from "../Filters/codeFilter";
import { DateFilter } from "../Filters/dateFilter";
import { SearchFilter } from "../Filters/searchFilter";
import { ModalLogSelected } from "../ModalLogSelected/ModalLogSelected";
import { useLogTableData } from "./customHooks/useLogTableData";

import "ag-grid-enterprise";
import moment from "moment";
import { ButtonWithLoading } from "../../../../../components/shared/buttons/button-with-loading";
import { useContextMenuMESF } from "../../../../../controls";
import { SearchData } from "../../models/SearchData";
import { useStyles } from "../../styles/tableStyles";
import { useLogsOptionsFunctions } from "./customHooks/useLogsOptionsFunctions";

interface Props {
	isLoading: boolean;
	rowData: Log[];
	refreshData: ({
		Start,
		End,
		Search,
		LogTypeCode,
	}: SearchData) => Promise<(() => void) | undefined>;
}

export const TableLogs = ({ isLoading, rowData, refreshData }: Props) => {
	const {
		state: { searchData: { Start, End, Search, LogTypeCode } },
		actions: {
			setLogTypeCodeFilter,
			setEndFilter,
			setStartFilter,
			setSearchFilter,
		},
	} = useLogsContext();
	const classes = useStyles();

	const gridStyle = useMemo(() => ({ height: "100%", width: "100%" }), []);
	const [gridAPI, setGridAPI] = useState<GridApi | null>(null);

	const [showLogModal, setShowLogModal] = useState<boolean>(false);

	const [selectedLog, setSelectedLog] = useState<Log | undefined>(undefined);
	const [error, setError] = useState<string>("");
	const onGridReady = (params: GridReadyEvent) => {
		setGridAPI(params.api);
	};

	const { rows, columnDefs } = useLogTableData();
	const defaultColDef = useMemo<ColDef>(() => {
		return {
			sortable: true,
			wrapText: true,
			autoHeight: true,
			wrapHeaderText: true,
		};
	}, []);

	const { showContextMenu, registerConfig } = useContextMenuMESF();

	const handleSubmit = (ev: React.FormEvent<HTMLFormElement>) => {
		ev.preventDefault();
		refreshData({ Start, End, Search, LogTypeCode });
	};
	const handleResetButtonClick = () => {
		resetFilter();
		refreshData({
			Start: moment().add(-5, "days").hour(0).minute(0).second(0).toDate(),
			End: moment().hour(23).minute(59).second(59).toDate(),
			Search: "",
			LogTypeCode: "A",
		});
		setError("");
	};

	const loadingOverlayComponent = useMemo<any>(() => {
		return (
			<div
				style={{
					width: "100%",
					height: "100%",
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
				}}
			>
				<LazyLoading />
			</div>
		);
	}, [isLoading]);

	// Btn reset data
	const resetFilter = () => {
		setStartFilter(null);
		setEndFilter(null);
		setSearchFilter("");
		setLogTypeCodeFilter("A");
		setSelectedLog(undefined);
	};

	const rowClicked = (rowClickedEvent: RowDoubleClickedEvent<any>) => {
		let data = rowClickedEvent.data;
		if (data) return;
		setSelectedLog(data as Log);
		setShowLogModal(true);
	};

	const getContextMenuItems = (
		params: GetContextMenuItemsParams,
	): (string | MenuItemDef)[] => {
		const data = params.node?.data;
		params.api.deselectAll();
		params.node?.setSelected(true);
		showContextMenu(event as any, data, "TableLogs");
		return [];
	};

	const { getMenuOptions } = useLogsOptionsFunctions({
		setSelectedLog,
		setShowLogModal,
		handleResetButtonClick,
	});

	useEffect(() => {
		registerConfig({
			id: "TableLogs",
			getOptions: getMenuOptions,
		});
	}, []);

	return (
		<Grid
			container
			justifyContent="center"
			alignItems="center"
			className={classes.root}
		>
			<Grid
				item
				md={12}
				xs={12}
				className={classes.filters}
				style={{ padding: "0 15px" }}
			>
				<ModalLogSelected
					show={showLogModal}
					onHide={() => setShowLogModal(false)}
					selectedLog={selectedLog}
				/>
				<Typography
					variant="h5"
					style={{ margin: "10px 0 6px", fontWeight: 600 }}
				>
					MES and System Logs
				</Typography>
				<Grid container direction="row" style={{ margin: "10px 0 0" }}>
					<Grid item md={12} xs={12}>
						<div className={classes.card}>
							<div style={{ padding: 8, width: "100%" }}>
								<form onSubmit={handleSubmit} className={classes.form}>
									<Grid
										container
										alignItems="center"
										direction="row"
										spacing={2}
									>
										<Grid item md={3} xs={12}>
											<DateFilter
												label="From"
												date={Start}
												setDate={setStartFilter}
											/>
										</Grid>
										<Grid item md={3} xs={12}>
											<DateFilter
												label="To"
												date={End}
												setDate={setEndFilter}
											/>
										</Grid>
										<Grid item md={2} xs={12}>
											<CodeFilter
												LogTypeCode={LogTypeCode}
												setLogTypeCodeFilter={setLogTypeCodeFilter}
											/>
										</Grid>
										<Grid
											item
											container
											md={4}
											xs={12}
											spacing={1}
											justifyContent="flex-end"
											alignItems="flex-end"
										>
											<Grid item md={4} xs={12}>
												<Button
													variant="contained"
													color="default"
													onClick={handleResetButtonClick}
													fullWidth
												>
													Reset
												</Button>
											</Grid>
											<Grid item md={4} xs={12}>
												<ButtonWithLoading
													isLoading={isLoading}
													fullWidth
													variant="contained"
													color="primary"
													type="submit"
												>
													Search
												</ButtonWithLoading>
											</Grid>
										</Grid>
									</Grid>
									<Grid container direction="row">
										<Grid item md={12} xs={12}>
											<SearchFilter
												search={Search}
												setSearch={setSearchFilter}
											/>
										</Grid>
									</Grid>
								</form>
							</div>
						</div>
					</Grid>
					<Grid
						item
						md={12}
						xs={12}
						style={{
							height: "55vh",
							margin: "6px 0 10px",
						}}
					>
						<div style={gridStyle} className="ag-theme-balham">
							<AgGridReact
								rowData={rows}
								columnDefs={columnDefs}
								defaultColDef={defaultColDef}
								onGridReady={onGridReady}
								getRowId={(params) => params.data.id}
								rowHeight={34}
								headerHeight={40}
								animateRows={true}
								loadingOverlayComponent={loadingOverlayComponent}
								getContextMenuItems={(e: any) => getContextMenuItems(e)}
								statusBar={{
									statusPanels: [
										{ statusPanel: "agTotalRowCountComponent", align: "left" },
										{
											statusPanel: "agFilteredRowCountComponent",
											align: "left",
										},
									],
								}}
								rowSelection="single"
								onRowDoubleClicked={(e) => {
									rowClicked(e);
								}}
							/>
						</div>
					</Grid>
				</Grid>
			</Grid>
			<ErrorModal error={error} onHide={() => setError("")} />
		</Grid>
	);
};
